import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import TranslationWrapper from "../../components/TranslationWrapper/TranslationWrapper";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import MainNavbar from "../../components/Navbars/MainNavbar";
import { Box, Typography } from "@mui/material";
import ChangelogV2 from "./Tabs/ChangelogV2";
import AuthenticationWrapper from "../../auth/AuthenticationWrapper";
import { makeStyles } from "tss-react/mui";
import { blackColor, grayColor, hexToRgb, primaryColor } from "../../assets/jss/material-dashboard-pro-react";
import "./JamezzFormV2Fix.css";
import FormV2 from "./FormV2";
import { useLocation } from "react-router";
import { useParams } from "react-router";

const useStyles = makeStyles()((theme) => ({
  flexContainer: {
    display: "block",
    [theme.breakpoints.down("xs")]: {
      display: "flex",
      flexWrap: "wrap",
    },
  },
  root: { marginTop: "20px", paddingLeft: "0", marginBottom: "0", overflow: "visible !important" },
  fixed: { overflow: "visible !important" },
  pills: {
    float: "left",
    position: "relative",
    display: "block",
    padding: "10px 15px",
    color: grayColor[6],
    height: "auto",
    opacity: "1",
    margin: "0 5px",
    fontSize: "12px",
    maxWidth: "100%",
    minWidth: "100px",
    minHeight: "48px",
    textAlign: "center",
    transition: "all .3s",
    fontWeight: "500",
    lineHeight: "24px",
    borderRadius: "30px",
    textTransform: "uppercase",
    letterSpacing: "initial",
  },

  displayNone: { display: "none !important" },
  primary: {
    "&,&:hover": {
      color: "white !important",
      backgroundColor: primaryColor[0],
      boxShadow:
        "0 4px 20px 0px rgba(" +
        hexToRgb(blackColor) +
        ", 0.14), 0 7px 10px -5px rgba(" +
        hexToRgb(primaryColor[0]) +
        ", 0.4)",
    },
  },
}));

function TabPanel({ children, value, index, ...other }) {
  // return value === index && children;
  return (
    <Typography
      aria-labelledby={`simple-tab-${index}`}
      component={"div"}
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      role={"tabpanel"}
    >
      <Box p={3}>{value === index ? children : null}</Box>
    </Typography>
  );
}

/**
 *
 * @param props
 * @returns {Element}
 * @constructor
 */
function JamezzFormV2(props) {
  const { classes, cx } = useStyles();
  const location = useLocation();
  const params = useParams();

  const [model, setModel] = useState(null);

  const initialQueryParam = useMemo(() => {
    if (location.search != null) {
      let params = new URLSearchParams(location.search);
      const tabIndex = params.get("tabIndex");
      if (Number.isNaN(Number(tabIndex))) {
        let _tabs;
        if (typeof props.tabs === "function") {
          _tabs = props.tabs(model);
        } else {
          _tabs = props.tabs;
        }
        const index = _tabs.findIndex((tab) => tab.navigationKey === tabIndex);
        return Math.max(0, index);
      } else {
        return Number(tabIndex);
      }
    } else {
      return 0;
    }
  }, [model, props.tabs, location]);

  const [visibleViewIndex, setVisibleViewIndex] = useState(initialQueryParam);

  const changeLogUpdatedAt = useRef(new Date().toString());

  const title = useMemo(() => {
    return model != null && props.computedPageTitle != null ? props.computedPageTitle(model) : props.pageTitle;
  }, [model, props.computedPageTitle, props.pageTitle]);

  useEffect(() => {
    if (window.history.pushState) {
      const newurl =
        window.location.protocol +
        "//" +
        window.location.host +
        window.location.pathname +
        "?tabIndex=" +
        visibleViewIndex;
      window.history.replaceState({ path: newurl }, "", newurl);
    }
  }, [visibleViewIndex]);

  useEffect(() => {
    document.title = title;
  }, [title]);

  const endPoint = useMemo(() => {
    if (params.id) {
      return `${props.endPoint}/${params.id}`;
    } else {
      return props.endPoint;
    }
  }, [props.endPoint, params]);

  const tabs = useMemo(() => {
    let _tabs;
    if (typeof props.tabs === "function") {
      _tabs = props.tabs(model);
    } else {
      _tabs = props.tabs;
    }
    let changeLogRight = props.rootRight + ".tabs.changelog";
    if (params.id && !_tabs.find((tab) => tab?.permissionKey === changeLogRight)) {
      _tabs.push({
        permissionKey: changeLogRight,
        tabButton: <TranslationWrapper tDefault={"Changelog"} tKey={"global.jamezzform.tabs.changelog"} />,
        tabContent: <ChangelogV2 endPoint={endPoint} key={changeLogUpdatedAt.current} />,
      });
    }
    return _tabs;
  }, [props.tabs, endPoint, changeLogUpdatedAt.current, model]);

  const flexContainerClasses = useMemo(() => {
    return cx({
      [classes.flexContainer]: true,
    });
  }, []);

  const pillsClasses = useMemo(() => {
    return cx({
      [classes.pills]: true,
    });
  }, []);

  return (
    <>
      <MainNavbar
        paginaTitel={title}
        subject={
          (model && typeof props.pageTitleSubject === "function"
            ? props.pageTitleSubject(model)
            : props.pageTitleSubject) ?? null
        }
      />
      <FormV2
        {...props}
        onModelReady={(model) => {
          setModel(model);
        }}
        visibleViewIndex={visibleViewIndex}
      >
        <Tabs
          classes={{
            root: classes.root,
            fixed: classes.fixed,
            flexContainer: flexContainerClasses,
            indicator: classes.displayNone,
          }}
          onChange={(e, index) => {
            setVisibleViewIndex(index);
          }}
          value={visibleViewIndex}
        >
          {tabs.map((tab) => (
            <AuthenticationWrapper
              child={
                <Tab
                  classes={{
                    root: pillsClasses,
                    selected: classes["primary"],
                  }}
                  label={tab.tabButton}
                />
              }
              key={tab.key ?? tab.permissionKey ?? tab.tabButton.props?.tKey ?? tab.tabButton}
              permissionKey={tab.permissionKey ?? "tab." + (tab.tabButton.props?.tKey ?? tab.tabButton)}
            />
          ))}
        </Tabs>

        <div className={"jamezz-swipeable-views-container"}>
          {tabs.map((tab, index) => (
            <TabPanel
              index={String(index)}
              key={tab.tabButton.props?.tKey ?? tab.tabButton}
              value={String(visibleViewIndex)}
            >
              <AuthenticationWrapper
                child={
                  <>
                    {React.cloneElement(tab.tabContent, {
                      classes,
                      model,
                    })}
                  </>
                }
                guardMode={false}
                key={tab.tabButton.props?.tKey ?? tab.tabButton}
                permissionKey={"tab." + (tab.tabButton.props?.tKey ?? tab.tabButton)}
              />
            </TabPanel>
          ))}
        </div>
      </FormV2>
    </>
  );
}

JamezzFormV2.defaultProps = {
  primaryKey: "id",
  tabs: [],
  extraFields: {},
  revalidateMode: "onChange",
  mode: "onChange",
  extraFormProps: {},
  objectsExcludedAsRelation: {},
  excludedKeysInOutput: {},
};

JamezzFormV2.propTypes = {
  endPoint: PropTypes.string.isRequired,
  pageTitle: PropTypes.string.isRequired,
  computedPageTitle: PropTypes.func,
  pageTitleSubject: PropTypes.oneOf([PropTypes.object, PropTypes.func]),
  rootRight: PropTypes.string.isRequired,
  tabs: PropTypes.oneOf([PropTypes.array, PropTypes.func]).isRequired,
  primaryKey: PropTypes.string,
  extraFields: PropTypes.object,
  parseInput: PropTypes.object,
  parseOutput: PropTypes.object,
  onGetModel: PropTypes.func,
  revalidateMode: PropTypes.string,
  mode: PropTypes.string,
  extraFormProps: PropTypes.object,
  objectsExcludedAsRelation: PropTypes.object,
  excludedKeysInOutput: PropTypes.object,
  // setting model.current in onGetModel causes infinite recursion, hence onModelReady
  onModelReady: PropTypes.func,
};

export default JamezzFormV2;
